import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { FiPlus, FiCpu, FiGitBranch, FiZap, FiAward, FiPackage, FiMessageSquare, FiList, FiBarChart2, FiTarget } from 'react-icons/fi';
import { councilLabApiClient } from '../../services/councilLabApiClient';

// Multi-tool probe prompts — exercise the full query→draft→dispatch→
// synthesize loop. Distinct from "Quick Prompts" which are single-
// purpose templates; probes are workflow validations. Adding a new
// probe is a one-line push into this array.
//
// `descriptor` (Lab entries only) is a short subtitle shown below the
// label in the Lab grid — at-a-glance read without hovering tooltip.
//
// `id` follows the L2-{TAB}-{NUM} ID-stamp convention (per
// docs/dev/agentic-test-prompts.md naming) so future operators can
// trace each Lab template back to a documented baseline session.
//
// `default_pack` flags the Wave-2 persona pack the prompt is designed
// to surface in `roster()` selection. Used as metadata for picker-
// reach validation; not enforced at runtime.
//
// `baseline` cross-references the agentic-test-prompts.md test family
// (W2-A, W2-B, W2-C, T1, etc.) when applicable.
interface ProbePrompt {
  id?: string;
  label: string;
  title: string;
  prompt: string;
  descriptor?: string;
  default_pack?: string;
  baseline?: string;
}

// Lab v2 (2026-05-06): the flat 9-template list grew into a tabbed
// fan-out across thematic sections. Each tab is a "bench" — a focused
// quiver of prompts the operator can fire against the substrate.
// Per the dual-triad orbital review, sections are domain-flavored
// (Picker's Quiver / AI-DevOps Bench / System Ops Bench / Storytelling
// Studio / Reflective Practice) rather than verb-tabbed; PM tab and
// Adversarial Sandbox deferred to v3 per the grounded YAGNI guard.
// v2.1 expansion (2026-05-06): operator-driven add of Compose Bench
// (Docker Compose stack design) + Hypervisor Bench (Proxmox + VM/LXC)
// for homelab + small-enterprise picker reach.
interface LabTab {
  id: string;
  emoji: string;
  label: string;          // short label for the tab strip
  descriptor: string;     // tagline shown in the active-tab header
  templates: ProbePrompt[];
  // v0.1 Hill Climbers — optional action chips that fire a backend call
  // instead of inserting a prompt. Rendered below templates with a violet
  // palette so they read as "execute" rather than "compose."
  actions?: LabAction[];
}

interface LabActionContext {
  sessionId: string | undefined;
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface LabAction {
  id: string;
  label: string;
  descriptor: string;
  run: (ctx: LabActionContext) => Promise<void>;
}
const PROBE_PROMPTS: ProbePrompt[] = [
  {
    label: 'Blocker report + peer review',
    title:
      'Find attention items, draft a report, dispatch to 3 peers, synthesize. '
      + 'Exercises attention_items + consult_peers + synthesis — the full '
      + 'query→draft→dispatch→synthesize loop. ~100-120s, 5 rounds.',
    prompt:
      'Find the current blockers in the task system, draft a brief written report, '
      + 'ask 3 colleagues to review it, then give me a synthesized recommendation.',
  },
];

// 🚧 Lab templates — abstract, context-aware versions of the
// exploratory probes. Each one is intentionally generic: scope drawn
// from "our current thread" / "what we have" so the persona can run
// the template as-is using ambient chat context, OR the user can drop
// it into the compose box and tweak the specifics before sending.
// Tucked behind a sub-flyout (clicked from the main menu) so the
// operator's daily compose chrome stays lean.
// 🧪 Lab v2 — tabbed fan-out across thematic sections. Each tab is
// a "bench" — a curated quiver of prompts the operator can fire
// against the substrate. Synthesis from the dual-triad orbital review
// (creative × product × grounded): domain-axis tabs (creative wins
// the framing), 5 tabs total (between grounded's 4 and creative's 7),
// 6-10 prompts per tab. PM Mosh Pit + Adversarial Sandbox + Spotlights
// deferred to v3 per grounded YAGNI guard.
const LAB_TABS: LabTab[] = [
  // ────────────────────────────────────────────────────────────────
  // 🎯 Picker's Quiver — picker-reach prompts spanning Wave-2 packs
  // ────────────────────────────────────────────────────────────────
  {
    id: 'quiver',
    emoji: '🎯',
    label: "Picker's Quiver",
    descriptor: "Wave-2 picker-reach prompts — fire one at the corpus and watch it fan out",
    templates: [
      {
        id: 'L2-PQ-01',
        baseline: 'W2-A',
        default_pack: 'transportation_infrastructure',
        label: '🛣 Streetscape redesign — adversarial panel',
        descriptor: 'Two-block downtown — highway engineer vs pedestrian designer',
        title:
          'W2-A baseline. Tests transportation + urban-planning pack reach. The '
          + 'minimax-overridden adversarial pair (Highway_Engineer_Conservative + '
          + 'Pedestrian_Streetscape_Designer) is the load-bearing test signal.',
        prompt:
          'I want to redesign Main Street in a small downtown — lanes are too wide, '
          + 'drivers go 45mph, sidewalks are 5 feet. Two-block stretch. Pull me a panel '
          + 'that disagrees productively.',
      },
      {
        id: 'L2-PQ-02',
        baseline: 'W2-B',
        default_pack: 'urban_planning_development',
        label: '🏘 Housing strategy clash — supply vs permanence',
        descriptor: '200K-pop city w/ rising rents — YIMBY vs CLT vs developer',
        title:
          'W2-B baseline. Picker should reach YIMBY (supply legalization) vs CLT '
          + '(permanent affordability) vs Real Estate Developer for theory-of-change '
          + 'disagreement. Domain-explicit but adversarial-implicit.',
        prompt:
          'City of 200K, rising rents, housing shortage. What\'s the right strategy?',
      },
      {
        id: 'L2-PQ-03',
        baseline: 'W2-C',
        default_pack: 'governance_ethics',
        label: '🤖 AI hiring governance — privacy + ethics + policy',
        descriptor: 'Deploying AI in hiring — what to flag for governance review',
        title:
          'W2-C baseline. Tests governance_ethics pack (AI policy counsel, privacy '
          + 'advocate, ethics-integrity practitioner). First turn answers direct; '
          + 'follow-up "pick a panel" turn tests panel composition.',
        prompt:
          'A company wants to deploy AI in hiring decisions. Walk me through the '
          + 'governance concerns I should raise.',
      },
      {
        id: 'L2-PQ-04',
        default_pack: 'humanitarian_emergency_ops',
        label: '🌪 Disaster cash logistics — vouchers vs in-kind',
        descriptor: '72-hour response in flood-affected coastal corridor',
        title:
          'Tests humanitarian_emergency_ops pack reach. Should pull cash-voucher '
          + 'programming lead + humanitarian logistics coordinator + emergency '
          + 'management director + protection officer. NO health-coded personas '
          + '(carve-out per spec).',
        prompt:
          'A category-3 hurricane just made landfall in a country with patchy NGO '
          + 'coverage and a flooded coastal corridor. I need a 72-hour response plan '
          + 'that splits cash-voucher programming from in-kind aid where each works '
          + 'best. What\'s the right cut and who needs to coordinate?',
      },
      {
        id: 'L2-PQ-05',
        default_pack: 'finance_operations',
        label: '💰 Treasury vs growth — runway under volatility',
        descriptor: 'CFO + FP&A + buy-side analyst on a runway-cut decision',
        title:
          'Tests finance_operations pack reach. Should pull treasury operations '
          + 'manager + financial planning analyst + long-term buy-side analyst + '
          + 'M&A practitioner. Stewardship-leaning voices (no short-seller).',
        prompt:
          'We\'re 18 months runway on the current burn but the GTM team is asking '
          + 'for $4M more for an aggressive H2 push. Treasury says cash position '
          + 'is too thin for what\'s coming in the public markets. Walk me through '
          + 'the right framing for this debate and who should weigh in.',
      },
      {
        id: 'L2-PQ-06',
        default_pack: 'energy_systems',
        label: '⚡ Grid storage bake-off — Li-ion vs pumped hydro vs flow',
        descriptor: '200MW peaker replacement — cost + reliability + lifecycle',
        title:
          'Tests energy_systems pack reach. Should pull energy storage engineer + '
          + 'grid operations engineer + transmission planner + renewables developer. '
          + 'Adversarial: each storage technology has structural advocates.',
        prompt:
          'We\'re replacing a 200MW peaker plant with grid-scale storage. The choice '
          + 'is lithium-ion vs pumped hydro vs flow batteries. Get me a panel of grid '
          + 'operations + storage engineers + transmission planners + a renewables '
          + 'developer to debate the cost-per-MWh + reliability + lifecycle tradeoffs.',
      },
      {
        id: 'L2-PQ-07',
        default_pack: 'visual_storytelling_production',
        label: '🎞 Edit-bay brawl — verite vs explanatory cuts',
        descriptor: 'Documentary editor + colorist + experimental compositor on cut order',
        title:
          'Tests visual_storytelling_production pack reach with the cross-model '
          + 'overrides (Documentary_Video_Editor + Experimental_Visual_Compositor on '
          + 'glm-4.7). Voice fidelity test — these are deliberately distinct registers.',
        prompt:
          'We\'re cutting a 90-minute documentary about a labor organizing campaign. '
          + 'The director wants pure verite; the studio wants explanatory cuts with '
          + 'on-screen titles. Pull me a panel of post-production folks to argue this — '
          + 'documentary editor, colorist, sound designer, and post-production supervisor.',
      },
      {
        id: 'L2-PQ-08',
        default_pack: 'narrative_design_transmedia',
        label: '📖 Story across media — novel + film + ARG cohesion',
        descriptor: 'What holds a story-world together across media — novel, film, ARG?',
        title:
          'Tests narrative_design_transmedia pack reach including the minimax-override '
          + 'ARG_Architect (unconventional-form voice). Canon governance + cross-media '
          + 'cohesion stress-test. Label avoids leading with the "trans-" prefix per '
          + 'operator guidance — persona pack name unchanged (corpus-internal).',
        prompt:
          'We have a novel, a film adaptation in pre-production, and a marketing '
          + 'budget that wants to add an ARG. Get me a panel of narrative designers — '
          + 'novelist, film/TV writer, ARG architect, transmedia producer — to debate '
          + 'what canon-governance looks like and which medium leads.',
      },
      {
        id: 'L2-PQ-09',
        default_pack: 'meta_cognitive_reflective',
        label: '🤔 Decision retro — right call or just lucky?',
        descriptor: 'Reflective facilitator + decision-quality coach + structured skeptic review a botched call',
        title:
          'Tests meta_cognitive_reflective pack reach. Reflective_Facilitator + '
          + 'Decision_Quality_Coach + Structured_Skeptic. Separates outcome '
          + 'quality from decision quality without leaning on hindsight.',
        prompt:
          'Our last big architectural pivot worked out, but I\'m not sure the '
          + 'decision was good — we got lucky on adoption. Pull a panel that can '
          + 'separate decision quality from outcome quality and tell me what we '
          + 'actually got right vs what was noise.',
      },
      {
        id: 'L2-PQ-10',
        default_pack: 'sociotechnical_design',
        label: '👥 Conway\'s Law stewardship — re-org panel',
        descriptor: 'Org designer + sociotechnical thinker + Conway-Law steward on a re-org',
        title:
          'Tests sociotechnical_design pack reach. Should pull Org_Designer + '
          + 'Sociotechnical_Systems_Thinker + Conway_Law_Steward + Change_Management_'
          + 'Practitioner. Joint-optimization framing of social + technical change.',
        prompt:
          'We\'re proposing a 4-team re-org around platform vs product. Before we '
          + 'ship the chart, get me a panel that can pressure-test it — how does the '
          + 'org change reflect the system architecture, what cognitive load are we '
          + 'shifting, and what are the Conway\'s Law consequences?',
      },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // 🛠 AI-DevOps Bench — AI-augmented operations at scale
  // ────────────────────────────────────────────────────────────────
  // Each prompt sets up an AI-augmented DevOps use case where AI
  // handles generation / classification / translation at scale and
  // humans keep the judgment + correction. Renamed from "DevSlops"
  // (2026-05-06 r2 sweep) — earlier framing leaned on a kitchen-slop
  // metaphor that read as ugly to fresh operators; the bench now sits
  // parallel to System Ops Bench in tone.
  {
    id: 'aiops',
    emoji: '🛠',
    label: 'AI-DevOps Bench',
    descriptor: "AI handles the toil generation; humans keep the judgment",
    templates: [
      {
        id: 'L2-DS-01',
        default_pack: 'devops_sre',
        label: '🚨 AI-augmented incident commander',
        descriptor: 'Sev-2 hits — AI orchestrates the runbook, you keep the judgment',
        title:
          'Tests devops_sre + meta_engineering reach. Post_Incident_Retro_Facilitator '
          + '+ Internal_Tools_Maintainer + an SRE. The "AI does generation, humans '
          + 'do judgment" abundance framing — one IC covers what 3 used to.',
        prompt:
          'It\'s 3am. Sev-2. Pull a panel — incident responder + post-incident retro '
          + 'facilitator + an SRE who\'s run AI-augmented runbooks — to design how '
          + 'the on-call workflow looks when AI does the blast-radius diff, drafts '
          + 'the comms thread, queues the rollback-vs-hotfix decision tree, and '
          + 'you\'re left to choose. What does the human still own? What gets '
          + 'handed off to the AI at scale?',
      },
      {
        id: 'L2-DS-02',
        default_pack: 'devops_sre',
        label: '🧹 Toil-to-AI conversion matrix',
        descriptor: '40 cron jobs — AI orchestrates which, replaces which, retires which',
        title:
          'Tests devops_sre + meta_engineering. Developer_Velocity_Researcher + '
          + 'Paved_Road_Steward + Internal_Tools_Maintainer. AI as the toil-'
          + 'classification + conversion engine.',
        prompt:
          'We have ~40 cron jobs scattered across our infra. Pull a panel — SREs + '
          + 'a developer-velocity researcher + a paved-road steward + an internal-'
          + 'tools maintainer — to classify each into AI-augment / AI-replace / '
          + 'retire. The deliverable is the conversion matrix: which jobs become '
          + 'AI-orchestrated workflows, which are wholesale replaced by an AI '
          + 'agent, which fall off. Be honest about what the AI can actually do here.',
      },
      {
        id: 'L2-DS-03',
        default_pack: 'devops_sre',
        label: '🔄 AI-co-piloted IaC migration',
        descriptor: 'Terraform → Pulumi mid-quarter — AI does code-translation + validation',
        title:
          'Tests devops_sre + architecture + meta_engineering. AI as the code-'
          + 'translator at scale; humans focus on the risky 5% review surface.',
        prompt:
          'Leadership wants Terraform → Pulumi mid-quarter and the platform team is '
          + 'split. Pull a panel — architects + devops + paved-road steward — to '
          + 'design an AI-co-piloted migration: AI generates code translations at '
          + 'scale, runs a validation harness against staging, surfaces the risky '
          + '5% for human review. End with engineering-weeks budget and what '
          + 'cognitive load humans still own.',
      },
      {
        id: 'L2-DS-04',
        default_pack: 'devops_sre',
        label: '🤖 AI-mediated Sev-2 triage agent',
        descriptor: 'LLM as first-line on-call — handles 80% routine, escalates 20% with full context',
        title:
          'Tests devops_sre + governance_ethics. AI_Policy_Counsel + Ethics_'
          + 'Integrity_Practitioner + a senior SRE. Operational + ethical risk '
          + 'of AI-mediated incident response — the abundance use case.',
        prompt:
          'Director wants an LLM in the on-call rotation as a first-line triage '
          + 'agent. Pull a panel — SREs + AI policy counsel + a devops leader — to '
          + 'design the abundant version: AI handles 80% of routine alerts (acks, '
          + 'deduplicates, runs known runbooks), escalates the 20% that need '
          + 'judgment with full context + audit trail. Tell me where the AI '
          + 'succeeds, where it fails, and what the trust contract looks like with '
          + 'engineers fielding these escalations.',
      },
      {
        id: 'L2-DS-05',
        default_pack: 'devops_sre',
        label: '💰 AI-cost-modeled platform decision',
        descriptor: 'K8s vs VMs — AI quantifies our actual on-call burden across workloads',
        title:
          'Tests devops_sre + meta_engineering + architecture + finance_operations. '
          + 'AI as the decision-data engine; data-driven platform choice instead of '
          + 'tribal debate.',
        prompt:
          'We\'re 18 months into Kubernetes. Half the team wants to stay; half '
          + 'wants to migrate back to VMs. Pull a panel — platform engineers + '
          + 'paved-road steward + developer-velocity researcher + a treasury-savvy '
          + 'operator — to design an AI-cost-modeled decision: AI ingests on-call '
          + 'hours + workload mix + cluster spend + team capacity, outputs a '
          + 'defensible recommendation. Tell me what the AI quantifies and what\'s '
          + 'still preference.',
      },
      {
        id: 'L2-DS-06',
        default_pack: 'devops_sre',
        label: '♻ AI-driven flag retirement pipeline',
        descriptor: '1200 feature flags — AI classifies, archives, drafts deletion PRs',
        title:
          'Tests devops_sre + architecture + frontend_ui. Internal_Tools_Maintainer '
          + '+ a paved-road steward should anchor. Operator reviews + merges; AI '
          + 'generates the cleanup at scale.',
        prompt:
          'We have 1200 feature flags. Engineering says they can\'t delete any '
          + 'safely. Pull a panel — SREs + frontend a11y + Internal Tools '
          + 'Maintainer + a developer-velocity researcher — to design an AI-driven '
          + 'retirement pipeline: AI surveys flag usage in logs, classifies into '
          + 'checked / stale / unused, drafts deletion PRs in batches, surfaces the '
          + 'high-risk ones for human review. End with the rollout plan and how '
          + 'many flags retire per week.',
      },
      {
        id: 'L2-DS-07',
        default_pack: 'devops_sre',
        label: '📊 AI-translated observability stack swap',
        descriptor: 'Datadog → OSS — AI translates dashboards, alerts, the cost case',
        title:
          'Tests devops_sre + finance_operations. Strategic_Procurement_Lead + '
          + 'Long_Term_Buy_Side_Analyst + an SRE. AI as the translator + cost '
          + 'modeler — turns a 6-month migration into a 6-week one.',
        prompt:
          'Datadog wants $90k/month. Pull a panel — SRE + cost-conscious platform '
          + 'engineer + procurement specialist + treasury operator — to scope an '
          + 'AI-translated migration to Loki + Grafana + Tempo: AI maps Datadog '
          + 'dashboards to OSS equivalents, AI re-classifies alerts under the new '
          + 'stack, AI drafts the cost-savings case for the CFO. End with the '
          + 'actual cost-and-risk delta and what humans still need to validate.',
      },
      {
        id: 'L2-DS-08',
        default_pack: 'meta_cognitive_reflective',
        label: '📝 AI-scribed incident retros at scale',
        descriptor: 'AI drafts the narrative — humans correct + add what AI missed',
        title:
          'Tests meta_cognitive_reflective + sociotechnical_design + devops_sre. '
          + 'Reflective_Practice_Supervisor + Group_Dynamics_Specialist + Post_'
          + 'Incident_Retro_Facilitator. AI removes the human-scribe hierarchy '
          + 'effect; humans focus on judgment + nuance.',
        prompt:
          'Incident retros get formulaic — same template, same energy, same misses. '
          + 'Pull a panel — facilitators + reflective-practice supervisor + '
          + 'sociotechnical-systems thinker + an SRE — to design AI-augmented '
          + 'retros. The AI ingests the incident timeline, drafts a blameless '
          + 'narrative, surfaces non-obvious causal patterns. Humans correct, add '
          + 'what AI missed, and own the action items. What\'s gained in honesty '
          + 'and what\'s lost in interpretive nuance?',
      },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // 🔧 System Ops Bench — architecture under load
  // ────────────────────────────────────────────────────────────────
  {
    id: 'sysops',
    emoji: '🔧',
    label: 'System Ops Bench',
    descriptor: "architecture decisions, performance, data-path design — substrate-grade engineering",
    templates: [
      {
        id: 'L2-SO-01',
        default_pack: 'architecture',
        label: '🏗 Monolith vs microservices — w/ your actual on-call cost',
        descriptor: 'The eternal debate, scoped to YOUR runtime',
        title:
          'Tests architecture + meta_engineering + devops_sre. Should pull '
          + 'Backend_Distributed_Systems_Engineer + Abstraction_Archaeologist + '
          + 'Developer_Velocity_Researcher. Scope-the-debate-to-actual-load.',
        prompt:
          'We have a Python monolith that\'s 250k lines, growing 30% YoY, and the '
          + 'platform team is making noise about microservices. Pull a panel — an '
          + 'architect + a distributed-systems specialist + a developer-velocity '
          + 'researcher + a paved-road steward — and have them argue for and '
          + 'against the migration *given our actual scale and team size*. Don\'t '
          + 'let them generalize.',
      },
      {
        id: 'L2-SO-02',
        default_pack: 'core_engineering',
        label: '🔭 Tracing strategy — Jaeger sampling at 100% is burning your storage budget',
        descriptor: 'Pick a sane policy that survives growth',
        title:
          'Tests core_engineering + devops_sre + architecture. Performance_'
          + 'Optimization_Engineer + Backend_Distributed_Systems_Engineer should '
          + 'surface. Adaptive vs head-based vs tail-based sampling tradeoffs.',
        prompt:
          'We\'re sampling at 100% in Jaeger and our trace storage costs just '
          + 'doubled. Get me a panel of observability-savvy engineers — distributed '
          + 'systems, performance optimization, SRE — to debate the right sampling '
          + 'policy. I want adaptive vs head-based vs tail-based, with actual '
          + 'reasoning about what we lose.',
      },
      {
        id: 'L2-SO-03',
        default_pack: 'core_engineering',
        label: '🔥 Read-path hot spot — 60% of DB load, three fixes',
        descriptor: 'Index, cache, or denormalize — what\'s the maintenance cost?',
        title:
          'Tests core_engineering + data_ai. Database_Internals_Expert + '
          + 'Performance_Optimization_Engineer + Backend_Distributed_Systems_Engineer.',
        prompt:
          'One endpoint is 60% of our database load. We\'ve got three proposed '
          + 'fixes on the table: add a covering index, add a Redis cache layer, or '
          + 'denormalize the read model. Pull a panel — database internals expert + '
          + 'performance engineer + backend distributed systems engineer — and '
          + 'have them argue the merits of each, including the maintenance cost '
          + 'we\'re not thinking about.',
      },
      {
        id: 'L2-SO-04',
        default_pack: 'architecture',
        label: '📡 Event bus vs RPC — when does decoupling earn its cost?',
        descriptor: 'Choreography vs orchestration in a real service mesh',
        title:
          'Tests architecture + core_engineering. Event_Driven_Architect + '
          + 'Abstraction_Archaeologist should anchor. When the cost of message '
          + 'plumbing is worth the decoupling.',
        prompt:
          'We\'re at the choice point: do we keep adding gRPC services, or move '
          + 'to event-driven choreography with a Kafka backbone? Get me a panel — '
          + 'an event-driven architect + a backend distributed systems engineer + '
          + 'an architecture archaeologist — to debate when the decoupling is '
          + 'worth the operational complexity.',
      },
      {
        id: 'L2-SO-05',
        default_pack: 'data_ai',
        label: '🔄 DB migration window — zero-downtime schema change',
        descriptor: 'Three takes on rolling out a breaking schema migration',
        title:
          'Tests data_ai + core_engineering + devops_sre. Database_Internals_Expert '
          + '+ Backend_Distributed_Systems_Engineer + an SRE. Expand/contract vs '
          + 'shadow-read vs replica-swap.',
        prompt:
          'We have a breaking schema migration to ship. Three approaches on the '
          + 'table: dual-write expand/contract, shadow-read with eventual cutover, '
          + 'or an orchestrated read-replica swap. Pull a panel of DBAs + backend '
          + 'engineers + an SRE to argue the merits of each — what\'s the actual '
          + 'on-call cost, what\'s the rollback story, what surprises does each '
          + 'hide?',
      },
      {
        id: 'L2-SO-06',
        default_pack: 'core_engineering',
        label: '📈 Capacity planning at 10x — what breaks first?',
        descriptor: 'Growth assumes the curve continues — model the failure modes',
        title:
          'Tests core_engineering + devops_sre + meta_cognitive_reflective. '
          + 'Pre_Mortem_Specialist should anchor. Force the answer past the easy '
          + '"storage" answer to the actual non-obvious bottleneck.',
        prompt:
          'Leadership is forecasting 10x growth in 18 months. Pull a panel — '
          + 'capacity-planning-experienced SREs + a database internals expert + a '
          + 'distributed systems engineer + a pre-mortem specialist — to model '
          + 'what breaks FIRST. Not the easy answer (storage) but the actual '
          + 'non-obvious bottleneck.',
      },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // 🎬 Storytelling Studio — creative substrate, finally let creatives in
  // ────────────────────────────────────────────────────────────────
  {
    id: 'storytelling',
    emoji: '🎬',
    label: 'Storytelling Studio',
    descriptor: "narrative + visual + cross-medium storytelling · creative-corpus packs the engineering crowd usually skips",
    templates: [
      {
        id: 'L2-ST-01',
        default_pack: 'visual_storytelling_production',
        label: '🎞 Cut order argument — verite vs explanatory',
        descriptor: 'Documentary editor + colorist + sound supervisor on a final pass',
        title:
          'Tests visual_storytelling_production with cross-model overrides '
          + '(Documentary_Video_Editor on glm-4.7). Picture-lock decision under '
          + 'director-vs-studio tension.',
        prompt:
          'We have a documentary at picture lock. The director wants verite — let '
          + 'the subjects speak for themselves. The producer wants explanatory cuts '
          + 'with title cards explaining the context. Pull a panel of post folks to '
          + 'argue this through — documentary editor, colorist, sound designer, and '
          + 'post-production supervisor. Tell me what\'s lost in each direction and '
          + 'which serves the story.',
      },
      {
        id: 'L2-ST-02',
        default_pack: 'narrative_design_transmedia',
        label: '📚 Series canon defense — what\'s load-bearing',
        descriptor: 'Showrunner + novelist + game-narrative on what\'s load-bearing',
        title:
          'Tests narrative_design_transmedia. World_Building_Consultant + Film_TV_'
          + 'Writer + Novelist + Game_Narrative_Designer should anchor. Canon-'
          + 'governance under cross-medium pressure.',
        prompt:
          'We\'re 12 episodes into a sci-fi show. The canon says X about the world; '
          + 'the new showrunner wants to invent Y that contradicts it. '
          + 'Pull a panel — film/TV writer + novelist + game-narrative designer + '
          + 'world-building consultant — to argue what\'s load-bearing canon vs '
          + 'what\'s flexible. End with their recommendation on how to handle the '
          + 'discrepancy with the audience.',
      },
      {
        id: 'L2-ST-03',
        default_pack: 'visual_storytelling_production',
        label: '📱 Platform-native form — TikTok-vertical vs cinema-horizontal',
        descriptor: 'Same story beat, two formats — what wins where?',
        title:
          'Tests visual_storytelling_production + digital_taste_curation. Motion_'
          + 'Designer + Cinematographer + Digital_Culture_Editor + Niche_Down_'
          + 'Trendspotter. Format-as-form constraint.',
        prompt:
          'We have a story beat that we\'re shooting twice — once for TikTok-vertical '
          + 'short-form, once for cinema-horizontal feature integration. Pull a panel '
          + 'of cinematographers + motion designers + video editors + a digital-'
          + 'culture editor to debate what\'s actually different about each medium '
          + 'and where the storytelling lives differently.',
      },
      {
        id: 'L2-ST-04',
        default_pack: 'narrative_design_transmedia',
        label: '🕵 ARG conceit pitch — alternate-reality launch',
        descriptor: 'ARG architect + immersive theater + brand strategist on a launch',
        title:
          'Tests narrative_design_transmedia with the minimax-override ARG_Architect '
          + '(unconventional-form voice). Trust-contract framing for ARG design.',
        prompt:
          'We have a film launching in 6 months and a marketing team that wants an '
          + 'ARG to drive pre-release engagement. Pull a panel — ARG architect + '
          + 'immersive theater director + transmedia producer + brand-aesthetic '
          + 'critic — to pitch the conceit. What story-shape works? What\'s the '
          + 'player community we want? What\'s the trust contract?',
      },
      {
        id: 'L2-ST-05',
        default_pack: 'digital_taste_curation',
        label: '🎨 Taste critique — viral aesthetic, deep read',
        descriptor: 'Digital culture editor + design critic + brand-aesthetic critic',
        title:
          'Tests digital_taste_curation pack reach. Digital_Culture_Editor + '
          + 'Brand_Aesthetic_Critic + Niche_Down_Trendspotter + Generative_Art_'
          + 'Curator. Cultural-vs-fashion adjudication.',
        prompt:
          'There\'s a visual aesthetic that\'s gone viral on a specific subreddit/'
          + 'Discord/TikTok niche over the past 60 days (you pick one — make it '
          + 'specific to today\'s internet). Pull a panel of digital-culture editors '
          + '+ design critics + brand-aesthetic critics + a generative-art curator '
          + 'to do a deep read. What is it actually doing, what\'s it borrowing '
          + 'from, and is it culturally meaningful or fashion noise?',
      },
      {
        id: 'L2-ST-06',
        default_pack: 'narrative_design_transmedia',
        label: '🎤 Documentary ethics — story vs subject',
        descriptor: 'When does the story serve the subject vs the audience?',
        title:
          'Tests narrative_design_transmedia + governance_ethics. Documentarian + '
          + 'Storytelling_Editor + Ethics_Integrity_Practitioner. Subject-protection '
          + 'vs narrative-truth tension.',
        prompt:
          'We\'re making a documentary about a community organizing campaign that '
          + 'didn\'t end well. The cut we have is dramatic and viewers will love '
          + 'it — but it makes one of the subjects look worse than the reality. '
          + 'Pull a panel — documentarian + journalist + ethics-integrity '
          + 'practitioner + a storytelling editor — to debate the ethical line and '
          + 'what the right cut looks like.',
      },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // 🪞 Reflective Practice — slow down, think about thinking
  // ────────────────────────────────────────────────────────────────
  {
    id: 'reflective',
    emoji: '🪞',
    label: 'Reflective Practice',
    descriptor: "decision quality, pre-mortems, second-order effects · meta-cognitive depth on hard calls",
    templates: [
      {
        id: 'L2-RP-01',
        default_pack: 'meta_cognitive_reflective',
        label: '🔮 Pre-mortem — this ships in 90 days and fails',
        descriptor: 'Imagine the failure before it happens — find the embarrassing one',
        title:
          'Tests meta_cognitive_reflective. Pre_Mortem_Specialist + Structured_'
          + 'Skeptic + Second_Order_Effects_Steward + a loyal-opposition voice. '
          + 'Force the embarrassing failure modes, not the obvious ones.',
        prompt:
          'We\'re 90 days from shipping our biggest release of the year. Pull a '
          + 'panel of pre-mortem specialists + structured skeptics + a second-order '
          + 'effects steward + a loyal-opposition voice trained to argue the '
          + 'strongest version of the dissent. Imagine forward 91 days: it failed '
          + 'spectacularly. Walk me through the most likely failure modes — not '
          + 'the obvious ones, the ones we\'d be embarrassed about looking back.',
      },
      {
        id: 'L2-RP-02',
        default_pack: 'meta_cognitive_reflective',
        label: '🥷 Steelman the dissent — force rigorous opposition',
        descriptor: 'When the team agrees too much, manufacture rigorous opposition',
        title:
          'Tests meta_cognitive_reflective + critical_thinking. Structured_Skeptic '
          + '+ Meta_Reasoning_Specialist + a loyal-opposition voice (the persona '
          + 'tagged for steelmanning + dissent). Anti-consensus discipline; force '
          + 'the strongest version of the contrary case.',
        prompt:
          'I just walked out of a leadership offsite where everyone agreed on the '
          + 'strategic direction. That\'s making me nervous. Pull peers trained '
          + 'to argue the opposite credibly — a loyal-opposition voice + a '
          + 'structured skeptic + a meta-reasoning specialist — and have them '
          + 'rip apart the consensus. What would they argue if they had to take '
          + 'the opposite position with rigor? Force the dissent we couldn\'t '
          + 'find in the room.',
      },
      {
        id: 'L2-RP-03',
        default_pack: 'meta_cognitive_reflective',
        label: '⚖ Decision-quality audit — right call or just lucky?',
        descriptor: 'Separating outcome from process on a real decision',
        title:
          'Tests meta_cognitive_reflective. Decision_Quality_Coach + Structured_'
          + 'Skeptic + Meta_Reasoning_Specialist. Outcome-quality vs decision-'
          + 'quality separation.',
        prompt:
          'We made a big call 6 months ago and it worked out. I\'m not sure if the '
          + '*decision* was good or if we got lucky. Pull a decision-quality coach '
          + '+ a structured skeptic + a meta-reasoning specialist and walk me '
          + 'through a decision-quality audit. What was the process, what would '
          + 'have happened with the same process if outcome distributions had '
          + 'played differently, and what should we change?',
      },
      {
        id: 'L2-RP-04',
        default_pack: 'critical_thinking',
        label: '🗺 Argument-mapping drill — claim/evidence/warrant',
        descriptor: 'Real workplace conflict, surfaced as a structured argument',
        title:
          'Tests critical_thinking + meta_cognitive_reflective + sociotechnical_'
          + 'design. Argument-shape decomposition under interpersonal heat.',
        prompt:
          'I have a workplace conflict where two senior engineers disagree on a '
          + 'technical direction and the disagreement has gotten personal. Both are '
          + 'smart. Pull a panel — critical thinking specialists + a structured '
          + 'skeptic + a conflict mediator — and walk me through mapping their '
          + 'actual arguments: claim, evidence, warrant, and rebuttal. Where\'s '
          + 'the disagreement actually rooted?',
      },
      {
        id: 'L2-RP-05',
        default_pack: 'meta_cognitive_reflective',
        label: '🌀 Second-order effects — the obvious fix has a hidden cost',
        descriptor: 'Where does the easy answer actually break things?',
        title:
          'Tests meta_cognitive_reflective + sociotechnical_design. Second_Order_'
          + 'Effects_Steward + Conway_Law_Steward + Sociotechnical_Systems_Thinker. '
          + 'Force the third-order objection.',
        prompt:
          'We have an obvious-looking fix on the table for a problem we\'ve been '
          + 'circling. Pull a second-order-effects steward + a Conway\'s-Law '
          + 'steward + a sociotechnical systems thinker to walk me through what '
          + 'BREAKS when we ship the obvious fix. Not the first-order objection, '
          + 'the third-order one nobody saw.',
      },
      {
        id: 'L2-RP-06',
        default_pack: 'meta_cognitive_reflective',
        label: '🪞 Facilitator mirror — the meeting about the meeting',
        descriptor: 'Run the retro on how the retro is going',
        title:
          'Tests meta_cognitive_reflective + sociotechnical_design. Reflective_'
          + 'Facilitator + Group_Dynamics_Specialist + Sociotechnical_Systems_'
          + 'Thinker. Process-meta-process self-observation.',
        prompt:
          'Our team retros have gotten formulaic — we\'re going through the '
          + 'motions. Pull a reflective facilitator + a group dynamics specialist '
          + '+ a sociotechnical systems thinker. Have them coach me through '
          + 'running a meeting about HOW we\'re running our meetings. What\'s '
          + 'broken in the format? What rituals do we keep doing because we '
          + 'always have?',
      },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // 🐳 Compose Bench — Docker Compose stack design at any scale
  // ────────────────────────────────────────────────────────────────
  // v2.1 expansion (2026-05-06): operator-flagged need for homelab
  // + small-enterprise picker reach. Compose Bench = scale-agnostic
  // Compose-stack tradeoffs (paired with Hypervisor Bench for the
  // virtualization side). Targets devops_sre + architecture +
  // meta_engineering pack reach against scale-flavored prompts.
  {
    id: 'compose',
    emoji: '🐳',
    label: 'Compose Bench',
    descriptor: "Docker Compose stack design at any scale — homelab to small-enterprise",
    templates: [
      {
        id: 'L2-CB-01',
        default_pack: 'devops_sre',
        label: '🐳 Stack split — when does one compose file stop earning?',
        descriptor: 'Single-file vs multi-file vs override-driven — pick by scale',
        title:
          'Tests devops_sre + architecture + meta_engineering. Compose project '
          + 'organization at the breakpoint between hobby-scale and SMB. Single '
          + 'vs multi-file vs profile-driven vs override-stack tradeoffs.',
        prompt:
          'We\'ve got a 12-service compose stack that grew organically — now it\'s '
          + 'a 600-line docker-compose.yml that takes 90 seconds to validate. Pull '
          + 'a panel — devops engineer + paved-road steward + developer-velocity '
          + 'researcher + a backend distributed systems engineer — to argue the '
          + 'right next move. Single-file with profile-driven sub-stacks vs '
          + 'override-stack pattern (base + dev + prod) vs full multi-file project '
          + 'split. End with the rule of thumb: how many services / how much '
          + 'config-overlap before each pattern earns its keep.',
      },
      {
        id: 'L2-CB-02',
        default_pack: 'devops_sre',
        label: '🦊 Edge proxy bake-off — Caddy vs Traefik vs nginx',
        descriptor: 'Auto-cert vs label-driven vs classic config',
        title:
          'Tests devops_sre + architecture + frontend_ui. Reverse-proxy choice '
          + 'in front of a Compose stack — Caddy auto-LE simplicity vs Traefik '
          + 'Docker-label discovery vs nginx classic config. Operator-burden '
          + 'tradeoffs at homelab vs SMB scale.',
        prompt:
          'Compose stack with 8 user-facing services needs HTTPS + auto-cert + '
          + 'path/host routing. Three contenders on the table: Caddy (auto-Let\'s-'
          + 'Encrypt, simple Caddyfile), Traefik (Docker-label-driven dynamic '
          + 'discovery, dashboards built in), nginx (battle-tested, classic '
          + 'config, zero magic). Pull a panel — paved-road steward + an SRE + a '
          + 'developer-velocity researcher + an internal-tools maintainer — to '
          + 'argue the operator-burden tradeoffs. Where does each shine, where '
          + 'does each bite, what\'s the right pick for an 8-service homelab vs '
          + 'a 30-service SMB?',
      },
      {
        id: 'L2-CB-03',
        default_pack: 'devops_sre',
        label: '🔐 Secrets at compose scale — env-file vs sops/age vs Vault',
        descriptor: "Hobby env-files don't scale; what's the right next rung?",
        title:
          'Tests devops_sre + governance_ethics + meta_engineering. Secret-'
          + 'management ladder for compose — naive env-file → sops/age in-git → '
          + 'Vault → external-secrets operator. Threat model + operator burden '
          + 'per rung.',
        prompt:
          'Stack started with a .env file. Now we have 3 environments '
          + '(dev/staging/prod) + 2 contributors + 1 production deploy. Pull a '
          + 'panel — devops + AI policy counsel + ethics-integrity practitioner '
          + '+ a paved-road steward — to walk us up the secrets-management '
          + 'ladder. Naive env-file → sops/age encrypted-in-git → HashiCorp '
          + 'Vault → external-secrets operator. Tell me at what scale each rung '
          + 'pays back, what each costs in operator burden, and the threat model '
          + 'each one actually closes.',
      },
      {
        id: 'L2-CB-04',
        default_pack: 'devops_sre',
        label: '📦 Volume + backup strategy for stateful compose',
        descriptor: 'Postgres + Redis + uploads — what gets backed up, how, where',
        title:
          'Tests devops_sre + data_ai + architecture. Compose-stack stateful-'
          + 'data strategy — bind vs named volumes, Postgres PITR vs dumps, '
          + 'Redis AOF vs snapshots, MinIO mirror, off-site rotation. RTO per '
          + 'tier.',
        prompt:
          'Compose stack with Postgres + Redis + MinIO + an uploads dir. Need a '
          + 'layered backup story that survives a Sunday-morning DR exercise. '
          + 'Pull a panel — database internals expert + an SRE + a paved-road '
          + 'steward + a backup-savvy operator — to design it. Bind vs named '
          + 'volumes, Postgres dump vs WAL/PITR vs pg_basebackup, Redis '
          + 'snapshots vs AOF, MinIO mirror vs lifecycle, off-site rotation '
          + 'cadence. End with: what gets backed up, where, how often, and a '
          + 'realistic restore-time-objective for each tier.',
      },
      {
        id: 'L2-CB-05',
        default_pack: 'devops_sre',
        label: '🌐 Multi-host: when Compose stops fitting — Swarm vs k3s',
        descriptor: 'Single-host hits a wall — pick the right next-tier',
        title:
          'Tests devops_sre + architecture + meta_engineering. Single-host '
          + 'Compose limits hit. Swarm = "Compose-shaped multi-host" vs k3s = '
          + '"real K8s on a budget." Operator-burden delta + what breaks first '
          + 'when scaling.',
        prompt:
          'Compose stack outgrew the single VM — needs 3 nodes for redundancy + '
          + 'capacity. Two paths: Docker Swarm (Compose syntax extends; same '
          + 'mental model; in-house ops cheap) vs k3s (real K8s API; full '
          + 'ecosystem; bigger concept count). Pull a panel — backend '
          + 'distributed systems engineer + paved-road steward + an SRE + '
          + 'developer-velocity researcher — to argue. Tell me where each path '
          + 'leads when you scale up, when you scale out, when you hire, what '
          + 'breaks first, and the rough operator-burden delta between them.',
      },
      {
        id: 'L2-CB-06',
        default_pack: 'devops_sre',
        label: '🚀 CI/CD-driven Compose deploys',
        descriptor: 'Push to main → stack up cleanly without prod outage',
        title:
          'Tests devops_sre + meta_engineering + architecture. Compose-deploy '
          + 'pipeline — git push → image build → registry → host pull → '
          + 'graceful restart. Rolling vs blue-green at compose scale.',
        prompt:
          '5-service Compose stack on a single VM. Push to main currently means '
          + 'SSH + git pull + docker compose up -d. Want to graduate to git-'
          + 'push-driven deploys without going full K8s. Pull a panel — devops '
          + '+ paved-road steward + developer-velocity researcher + an internal-'
          + 'tools maintainer — to design the minimal pipeline. Build → '
          + 'registry push → host pull → graceful restart. Rolling vs blue-'
          + 'green at compose scale. What\'s the smallest pipeline that handles '
          + 'rollback cleanly, and where does the next-tier complexity start to '
          + 'be worth it?',
      },
    ],
  },
  // ────────────────────────────────────────────────────────────────
  // 🖥 Hypervisor Bench — Proxmox + VM/LXC + cluster ops
  // ────────────────────────────────────────────────────────────────
  // v2.1 expansion (2026-05-06): paired with Compose Bench. Proxmox-
  // flavored picker reach for VM/LXC routing, storage backend choice,
  // PBS strategy, networking design, live migration. Operator runs
  // Proxmox in his homelab — these prompts are the field-test surface
  // for the platform-engineering pack reach.
  {
    id: 'hypervisor',
    emoji: '🖥',
    label: 'Hypervisor Bench',
    descriptor: "Proxmox + VM/LXC + cluster ops — homelab to small-enterprise",
    templates: [
      {
        id: 'L2-HB-01',
        default_pack: 'devops_sre',
        label: '🖥 Proxmox cluster — 2-node HA vs 3-node quorum',
        descriptor: 'When does the third node earn its electric bill?',
        title:
          'Tests devops_sre + architecture + finance_operations. Proxmox '
          + 'cluster topology at homelab/small-shop scale — 2-node + QDevice '
          + 'witness vs 3-node real cluster. HA + split-brain + maintenance '
          + 'tradeoffs.',
        prompt:
          'Building a Proxmox cluster for the homelab — budget covers 2 nodes '
          + 'comfortably, 3 nodes if I stretch. Pull a panel — an SRE + backend '
          + 'distributed systems engineer + a paved-road steward + a treasury-'
          + 'savvy operator — to argue 2-node-with-QDevice (raspberry-pi-as-'
          + 'witness for quorum) vs committing to 3 real nodes. Tell me where '
          + 'the QDevice setup falls over, when the third node earns its '
          + 'electric bill, what split-brain looks like in each topology, and '
          + 'how maintenance windows feel when one node is down.',
      },
      {
        id: 'L2-HB-02',
        default_pack: 'devops_sre',
        label: '🏷 LXC vs VM in Proxmox — when each earns its overhead',
        descriptor: 'Density vs isolation vs portability — pick per workload',
        title:
          'Tests devops_sre + architecture + core_engineering. Workload-'
          + 'routing in a mixed Proxmox node — when LXC density beats VM '
          + 'isolation and where that rule of thumb breaks (kernel modules, '
          + 'GPU passthrough, kernel mismatches).',
        prompt:
          'Mixed-workload Proxmox node: Postgres + Plex + Pi-hole + Home '
          + 'Assistant + 3 dev VMs. Some workloads tolerate shared kernel, '
          + 'others don\'t. Pull a panel — backend distributed systems engineer '
          + '+ an SRE + a paved-road steward + a database internals expert — '
          + 'to route each workload. LXC for density + near-zero overhead vs '
          + 'VM for clean isolation + portability. Where do the rules of thumb '
          + 'hold, where do they break (kernel-module needs, kernel mismatches, '
          + 'GPU passthrough), and what\'s the right default when you\'re not '
          + 'sure?',
      },
      {
        id: 'L2-HB-03',
        default_pack: 'devops_sre',
        label: '💾 Storage backend — ZFS vs Ceph vs LVM-thin in Proxmox',
        descriptor: 'RAIDZ + snapshots vs distributed vs simple — pick by failure domain',
        title:
          'Tests devops_sre + data_ai + architecture. Proxmox storage choice '
          + 'across the single-node → cluster transition. ZFS snapshot/'
          + 'replication ergonomics vs Ceph cluster-distributed vs LVM-thin '
          + 'simplicity. Migration pain assessment.',
        prompt:
          'Single-node Proxmox today, planning a 3-node cluster in 6 months. '
          + 'Storage backend choice will follow me. Pull a panel — database '
          + 'internals expert + an SRE + backend distributed systems engineer + '
          + 'a paved-road steward — to compare ZFS (RAIDZ + snapshot/'
          + 'replication ergonomics; single-node star) vs Ceph (cluster-'
          + 'distributed; real HA but higher operator burden) vs LVM-thin '
          + '(simple, fast, fewer features). What makes sense at single-node, '
          + 'what migration pain looks like when I add nodes 2 and 3, and '
          + 'where each one bites in a recovery scenario.',
      },
      {
        id: 'L2-HB-04',
        default_pack: 'devops_sre',
        label: '🛟 PBS strategy — schedule + replication + offsite',
        descriptor: 'Proxmox Backup Server done right — encryption, dedup, the offsite',
        title:
          'Tests devops_sre + architecture + governance_ethics. Proxmox '
          + 'Backup Server design — encryption + retention + dedup tier + '
          + 'replication + off-site (B2/S3/second homelab). 3-2-1 mapped to '
          + 'real tiers.',
        prompt:
          'Proxmox Backup Server running on a separate node, backing up 6 VMs '
          + '+ 12 LXCs. Need to extend to bulletproof: encryption, retention '
          + 'windows, off-site rotation (B2 / S3 / second homelab), restore '
          + 'drills. Pull a panel — an SRE + AI policy counsel + a paved-road '
          + 'steward + a backup-savvy operator — to design the strategy. 3-2-1 '
          + 'mapped to actual storage tiers, schedule cadence vs change rate, '
          + 'retention math, and what a real Sunday-morning restore drill '
          + 'looks like end to end.',
      },
      {
        id: 'L2-HB-05',
        default_pack: 'devops_sre',
        label: '🌉 VLAN + vmbr networking design',
        descriptor: 'Tagged trunks + vmbrN bridges — when, how, what to isolate',
        title:
          'Tests devops_sre + architecture + core_engineering. Proxmox '
          + 'networking design — vmbr layout + VLAN-tagged trunks + access '
          + 'ports + segmentation (mgmt vs LAN vs DMZ vs IoT vs storage).',
        prompt:
          'Single-vmbr-everything Proxmox node about to grow into a real '
          + 'homelab — need separate segments for mgmt vs LAN services vs '
          + 'DMZ-facing vs IoT vs storage. Pull a panel — backend distributed '
          + 'systems engineer + an SRE + a paved-road steward + a security-'
          + 'aware operator — to design the network layout. vmbr0 / vmbr1 / '
          + 'etc layout, VLAN-tagged trunks vs access ports, switch config '
          + '(managed vs unmanaged), where the firewall lives, and how this '
          + 'looks under maintenance + failure.',
      },
      {
        id: 'L2-HB-06',
        default_pack: 'devops_sre',
        label: '🔁 Live migration + snapshot strategy',
        descriptor: 'Maintenance windows without downtime — when migration earns the storage cost',
        title:
          'Tests devops_sre + architecture + finance_operations. Proxmox live '
          + 'migration mechanics — shared storage (Ceph/NFS) vs replication-'
          + 'then-failover vs scheduled outages. Operator burden vs uptime '
          + 'cost-benefit.',
        prompt:
          '3-node Proxmox cluster, time to update kernels. Need to handle this '
          + 'without service downtime. Three paths: live migration over shared '
          + 'storage (Ceph or NFS) vs replication-then-failover vs scheduled '
          + '5-minute outage windows. Pull a panel — backend distributed '
          + 'systems engineer + an SRE + a paved-road steward + a treasury-'
          + 'savvy operator — to argue the tradeoffs. Where does shared-'
          + 'storage complexity earn its keep, when does replication-failover '
          + 'do the job for half the operator burden, and when is a scheduled '
          + 'window honestly the right answer?',
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // 🥾 Hill Climbers — v0.1 substrate exercisers
  // Action chips (not prompts) — they call council-lab backend endpoints
  // directly so the operator can drive the propose_fork → review →
  // approve loop from inside an active trail without spinning up a real
  // peer LLM. The dedicated /council-lab page is the cross-session
  // satellite for the same toolkit; these chips are the in-trail entry.
  // ────────────────────────────────────────────────────────────────
  {
    id: 'hill-climbers',
    emoji: '🥾',
    label: 'Hill Climbers',
    descriptor:
      'v0.1 substrate exercisers — fire actions at the live session, watch the chip + dialog tick',
    templates: [],
    actions: [
      {
        id: 'HC-01',
        label: 'Inject 1 propose_fork',
        descriptor:
          'Synthetic proposal from the session\'s first peer at their spine_root',
        run: async ({ sessionId, showToast }) => {
          if (!sessionId) {
            showToast('No session yet — start a chat first.', 'error');
            return;
          }
          const out = await councilLabApiClient.injectProposal(sessionId);
          showToast(`Injected ${out.kind} · ${out.id.slice(0, 8)}…`, 'success');
        },
      },
      {
        id: 'HC-02',
        label: 'Inject 5 propose_forks',
        descriptor: 'Bulk inject — exercises the chip count + dialog scroll',
        run: async ({ sessionId, showToast }) => {
          if (!sessionId) {
            showToast('No session yet — start a chat first.', 'error');
            return;
          }
          const out = await councilLabApiClient.injectProposals(sessionId, 5);
          showToast(`Injected ${out.count} proposals`, 'success');
        },
      },
      {
        id: 'HC-03',
        label: 'Inject 10 propose_forks',
        descriptor: 'Stress the chip count + dialog scrolling',
        run: async ({ sessionId, showToast }) => {
          if (!sessionId) {
            showToast('No session yet — start a chat first.', 'error');
            return;
          }
          const out = await councilLabApiClient.injectProposals(sessionId, 10);
          showToast(`Injected ${out.count} proposals`, 'success');
        },
      },
      {
        id: 'HC-04',
        label: 'Wipe pending proposals',
        descriptor: 'DELETE all pending rows · resolved rows preserved',
        run: async ({ sessionId, showToast }) => {
          if (!sessionId) {
            showToast('No session yet — start a chat first.', 'error');
            return;
          }
          const out = await councilLabApiClient.wipeProposals(sessionId);
          showToast(`Wiped ${out.wiped} pending proposal(s)`, 'info');
        },
      },
    ],
  },
];

// Convenience: total count across all tabs.
const LAB_TEMPLATE_TOTAL = LAB_TABS.reduce(
  (sum, t) => sum + t.templates.length + (t.actions?.length ?? 0),
  0,
);

// Construction-zone barber-pole stripes. Inline style because Tailwind
// can't express a `repeating-linear-gradient` with arbitrary stops. Used
// as the top + bottom band of the 🚧 Lab sub-flyout — the visual signal
// that the contents are experimental templates, not regular menu items.
const CONSTRUCTION_STRIPES = {
  backgroundImage:
    'repeating-linear-gradient(45deg, #fbbf24 0 8px, #1f2937 8px 16px)',
};

// localStorage key tracking whether the user has opened the Lab
// sub-flyout at least once. While unset, the +Compose chip wears a
// quiet amber pulse-dot and the "Lab" entry inside the menu wears a
// ✨ shimmer — both clear as soon as Lab opens. Reset for force-test
// via DevTools: `localStorage.removeItem('compose.labSeen')`.
const LS_LAB_SEEN_KEY = 'compose.labSeen';

const readLabSeen = (): boolean => {
  try {
    return localStorage.getItem(LS_LAB_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
};

const markLabSeen = (): void => {
  try {
    localStorage.setItem(LS_LAB_SEEN_KEY, 'true');
  } catch {
    // localStorage disabled (private mode, quota): pulse just
    // re-appears on next render. Fail silent — the dot is invitation,
    // not requirement.
  }
};

interface ComposeMenuProps {
  personaId?: string;
  personaName?: string;
  personaTags?: string[];
  sessionId?: string;
  onInsert: (text: string) => void;
}

const DIAGRAM_TEMPLATES: Record<string, string> = {
  flow: '```mermaid\nflowchart TD\n    A[Start] --> B{Decision}\n    B -->|Yes| C[Process]\n    B -->|No| D[Alternative]\n    C --> E[End]\n    D --> E\n```\n',
  seq: '```mermaid\nsequenceDiagram\n    actor User\n    participant System\n    User->>System: Request\n    System-->>User: Response\n```\n',
  class: '```mermaid\nclassDiagram\n    class Entity {\n        +String name\n        +method()\n    }\n```\n',
  er: '```mermaid\nerDiagram\n    ENTITY_A ||--o{ ENTITY_B : "relates"\n    ENTITY_B ||--|{ ENTITY_C : "contains"\n```\n',
  state: '```mermaid\nstateDiagram-v2\n    [*] --> Idle\n    Idle --> Active: trigger\n    Active --> Done: complete\n    Done --> [*]\n```\n',
};

const ComposeMenu: React.FC<ComposeMenuProps> = ({
  personaId,
  personaName = 'AI',
  personaTags = [],
  sessionId,
  onInsert,
}) => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  // Track per-action in-flight state so a double-click can't double-fire.
  // Key: `${tabId}:${actionId}`; value: true while the action is running.
  const [busyActionKey, setBusyActionKey] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // 🚧 Lab sub-flyout open state. Lives alongside `open` so the Lab can
  // remain expanded while the user reads the templates — closing the
  // main menu also closes the Lab (single click-outside handler covers
  // both because the Lab JSX is nested inside menuRef).
  const [showLab, setShowLab] = useState(false);
  // First-open invitation: pulse-dot on +Compose chip + shimmer on
  // Lab entry until the user opens Lab once. Initialized from
  // localStorage so the indicators don't re-appear on every page reload
  // for users who've already explored the Lab.
  const [hasSeenLab, setHasSeenLab] = useState<boolean>(readLabSeen);
  // Lab v2 tab state — defaults to first tab (Picker's Quiver per the
  // dual-triad ordering). Lives in component state, not localStorage,
  // so each Lab open starts fresh on the most-fired tab.
  const [activeTabId, setActiveTabId] = useState<string>(LAB_TABS[0].id);
  const activeTab = LAB_TABS.find((t) => t.id === activeTabId) ?? LAB_TABS[0];
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowLab(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Closing the main menu also collapses the Lab so the next open
  // starts from a clean state. Resetting on every close avoids the
  // surprise of "Lab silently re-opens because it was open last time."
  useEffect(() => {
    if (!open) setShowLab(false);
  }, [open]);

  const insert = (text: string) => {
    onInsert(text);
    setOpen(false);
    setShowLab(false);
  };

  // Hill Climbers action runner. Distinct from `insert` — these chips
  // fire backend calls and stay-open on success (operator may want to
  // chain a few injects before closing the menu). Failures surface via
  // the toast channel + busy state clears either way.
  const runAction = async (tabId: string, action: LabAction) => {
    const key = `${tabId}:${action.id}`;
    if (busyActionKey) return;
    setBusyActionKey(key);
    try {
      await action.run({ sessionId, showToast });
    } catch (e) {
      showToast(
        `${action.label}: ${(e as Error).message ?? 'failed'}`,
        'error',
      );
    } finally {
      setBusyActionKey(null);
    }
  };

  const sectionHeader = `px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
    darkMode ? 'text-gray-500' : 'text-gray-400'
  }`;
  const itemClass = `block w-full text-left px-3 py-1.5 text-xs transition-colors ${
    darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
  }`;
  const displayName = personaName.replace(/_/g, ' ');

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`relative flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
          open
            ? darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
            : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        aria-label={hasSeenLab ? 'Compose menu' : 'Compose menu — new Lab templates available'}
      >
        <FiPlus className="w-3 h-3" /> Compose
        {/* Quiet amber pulse-dot — invites operators to discover the Lab
            templates. Clears on first Lab-open via localStorage flag. */}
        {!hasSeenLab && !open && (
          <span
            data-testid="compose-lab-pulse"
            aria-hidden
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 animate-pulse shadow-[0_0_4px_rgba(251,191,36,0.6)]"
          />
        )}
      </button>

      {open && (
        <div className={`absolute left-0 bottom-full mb-1 w-72 rounded-lg shadow-xl z-20 border overflow-hidden ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Quick Prompts */}
          <div className={sectionHeader}>Quick Prompts</div>
          <button className={itemClass} onClick={() => insert(`As ${displayName}, give me your deepest analysis of `)}>
            <FiCpu className="inline w-3 h-3 mr-1.5 text-emerald-400" /> Deep dive analysis...
          </button>
          <button className={itemClass} onClick={() => insert('Please analyze the following:\n\n```\n[paste here]\n```\n\nFocus on key insights, issues, and recommendations.')}>
            <FiBarChart2 className="inline w-3 h-3 mr-1.5 text-blue-400" /> Analyze pasted content
          </button>
          <button className={itemClass} onClick={() => insert('Compare and contrast these approaches:\n\n**Option A:** \n\n**Option B:** \n\nEvaluate on: feasibility, cost, timeline, and risk.')}>
            <FiGitBranch className="inline w-3 h-3 mr-1.5 text-purple-400" /> Compare approaches
          </button>
          <button className={itemClass} onClick={() => insert(`What are your top 3 recommendations for improving `)}>
            <FiList className="inline w-3 h-3 mr-1.5 text-amber-400" /> Top recommendations for...
          </button>

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* Multi-tool Probes — canned prompts that validate the full
              workflow: query → draft → dispatch → synthesize. Drop into
              compose for review/edit before sending. */}
          <div className={sectionHeader}>Multi-tool Probes</div>
          {PROBE_PROMPTS.map((p, i) => (
            <button
              key={i}
              className={itemClass}
              title={p.title}
              onClick={() => insert(p.prompt)}
            >
              <FiTarget className="inline w-3 h-3 mr-1.5 text-rose-400" />
              {p.label}
            </button>
          ))}

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* 🚧 Lab — single entry that opens a sub-flyout to the right
              with the 6 abstract / context-aware multi-tool templates.
              Tucked away here so the main menu stays lean for daily
              compose, but the templates live one click deep with
              construction-zone visuals to telegraph "experimental." */}
          <button
            className={`block w-full text-left px-3 py-1.5 text-xs transition-colors ${
              showLab
                ? darkMode
                  ? 'bg-amber-900/30 text-amber-300'
                  : 'bg-amber-50 text-amber-700'
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => {
              setShowLab((v) => !v);
              // First-open invitation: clear the pulse + shimmer once
              // the user has discovered Lab. Persisted in localStorage
              // so this is a one-shot per browser/device.
              if (!hasSeenLab) {
                setHasSeenLab(true);
                markLabSeen();
              }
            }}
            aria-expanded={showLab}
            aria-controls="compose-lab-flyout"
            title="Multi-tool templates — abstract / context-aware. Run as-is or tweak."
          >
            <span className="inline-flex items-center w-full">
              <span className="mr-1.5">🚧</span>
              <span className="font-semibold tracking-wide">Lab</span>
              <span className={`ml-1.5 text-[10px] uppercase tracking-widest ${
                darkMode ? 'text-amber-500/70' : 'text-amber-600/80'
              }`}>
                · {LAB_TABS.length} tabs · {LAB_TEMPLATE_TOTAL} templates
              </span>
              {/* First-open shimmer — sparkle next to "Lab" until the
                  user has opened it once. Pairs with the pulse-dot on
                  +Compose; both clear via the same localStorage flag. */}
              {!hasSeenLab && (
                <span
                  data-testid="compose-lab-shimmer"
                  aria-hidden
                  className={`ml-1.5 animate-pulse ${
                    darkMode ? 'text-amber-300' : 'text-amber-600'
                  }`}
                >
                  ✨
                </span>
              )}
              <span className="ml-auto opacity-60">{showLab ? '◂' : '▸'}</span>
            </span>
          </button>

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* Diagrams */}
          <div className={sectionHeader}>Diagrams</div>
          <div className="flex gap-1 px-3 py-1.5">
            {Object.entries(DIAGRAM_TEMPLATES).map(([key, tmpl]) => (
              <button
                key={key}
                onClick={() => insert(tmpl)}
                className={`px-2 py-1 rounded text-[10px] font-medium ${
                  darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={`Insert ${key} diagram`}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />

          {/* Power Actions */}
          <div className={sectionHeader}>Conversation Actions</div>
          <button className={itemClass} onClick={() => insert('Please summarize our entire conversation so far. Include: key topics discussed, decisions made, and any open questions.')}>
            <FiMessageSquare className="inline w-3 h-3 mr-1.5 text-emerald-400" /> Summarize conversation
          </button>
          <button className={itemClass} onClick={() => insert('Extract all action items, decisions, and concrete next steps from our conversation. Format as a checklist.')}>
            <FiList className="inline w-3 h-3 mr-1.5 text-cyan-400" /> Extract action items
          </button>
          <button className={itemClass} onClick={() => insert('Based on our conversation, generate 3-5 probe questions that would test whether a persona truly understands the concepts we discussed.')}>
            <FiZap className="inline w-3 h-3 mr-1.5 text-amber-400" /> Generate probe questions
          </button>

          {/* Demoted from chip bar 2026-04-24 — situational patterns
              that earn a click here but don't deserve always-on chip
              real estate. Emoji prefix preserves their chip-bar
              identity for users who recognize them. */}
          <button
            className={itemClass}
            title="Protocol-driven session: persona asks one clarifying question at a time until it has enough to produce a deliverable."
            onClick={() =>
              insert(`Let's run a focused working session.

Protocol:
1. Ask me **one** clarifying question at a time — never a wall of questions
2. Wait for my answer before the next question
3. Continue until you have enough to produce a concrete deliverable (plan, draft, decision doc, code, whatever fits)
4. When you have enough, say "I have what I need" and produce it
5. If I go off-topic, gently pull us back

Start by asking me: **what's the deliverable, and when do I need it by?**`)
            }
          >
            <FiTarget className="inline w-3 h-3 mr-1.5 text-emerald-400" /> 🎯 Focus Session
          </button>
          <button
            className={itemClass}
            title="Generates a copy-pasteable handoff note for another persona picking up this thread cold."
            onClick={() =>
              insert(`Write a handoff note for another persona picking up this thread cold. Format:

**Handoff: <one-line topic>**

1. **Where we are** — the state of the work, in 2–3 sentences
2. **What's decided** — bullet list of decisions already made (with my reasoning for each)
3. **What's open** — bullet list of unresolved questions or choices
4. **What they need to know about me** — preferences, constraints, context that will make them effective from minute one
5. **Recommended next move** — what you'd do first if you were continuing

Keep it copy-pasteable. Minute-one effectiveness is the goal.`)
            }
          >
            <FiList className="inline w-3 h-3 mr-1.5 text-purple-400" /> 📝 Brief Next Person
          </button>
          <button
            className={itemClass}
            title="Surfaces the top 3 ways the current approach goes wrong, with severity + what would prevent each."
            onClick={() =>
              insert(`What's the most likely way our current approach goes wrong?

List the top 3 failure modes:
1. **Mode** + a one-line description of what triggers it
2. **Severity** (critical / major / minor) + what we lose if it happens
3. **What would prevent it** — concrete, actionable

End with the single failure mode you'd most want to defend against first, and why.`)
            }
          >
            <FiBarChart2 className="inline w-3 h-3 mr-1.5 text-rose-400" /> 🪤 Failure Modes
          </button>

          {/* Platform Links */}
          {personaId && (
            <>
              <div className={`h-px my-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className={sectionHeader}>Platform</div>
              <button className={itemClass} onClick={() => { setOpen(false); navigate(`/personas/forge?personaId=${personaId}`); }}>
                <FiPackage className="inline w-3 h-3 mr-1.5 text-purple-400" /> Open in Forge
              </button>
              <button className={itemClass} onClick={() => { setOpen(false); navigate('/gymnasium/arena'); }}>
                <FiAward className="inline w-3 h-3 mr-1.5 text-amber-400" /> Test in Gymnasium
              </button>
            </>
          )}
        </div>
      )}

      {/* 🚧 Lab v2 sub-flyout — opens to the right of the main menu
          when "Lab" is clicked. Tabbed structure: a horizontal tab
          strip at top selects one of 5 thematic sections (🎯 Quiver
          / 🛠 AI-DevOps / 🔧 System Ops / 🎬 Storytelling / 🪞 Reflective /
          🐳 Compose / 🖥 Hypervisor).
          Below the tab strip: header descriptor + 2-col fan-out grid
          of the active tab's templates. Construction-zone visuals
          (amber + barber-pole stripe bands) telegraph "experimental."
          Same click-outside handler dismisses both menus because this
          lives inside menuRef. Synthesis from the dual-triad orbital
          review (creative × product × grounded) on 2026-05-06. */}
      {open && showLab && (
        <div
          id="compose-lab-flyout"
          role="region"
          aria-label="Lab — multi-tool templates"
          className={`absolute left-72 bottom-full mb-1 ml-2 w-[40rem] flex flex-col rounded-lg shadow-2xl z-30 border overflow-hidden ${
            darkMode
              ? 'bg-gray-900 border-amber-500/40'
              : 'bg-amber-50 border-amber-300'
          }`}
        >
          {/* Top construction band */}
          <div className="h-2 w-full" style={CONSTRUCTION_STRIPES} aria-hidden />

          {/* Header — flyout-level, telegraphs the LAB brand */}
          <div className={`px-3 py-2 ${darkMode ? 'bg-gray-900' : 'bg-amber-50'}`}>
            <div
              className={`flex items-center gap-1.5 text-sm font-bold ${
                darkMode ? 'text-amber-300' : 'text-amber-800'
              }`}
            >
              <span aria-hidden>🚧</span>
              <span className="tracking-wider">LAB</span>
              <span
                className={`ml-auto text-[9px] uppercase tracking-widest font-mono ${
                  darkMode ? 'text-amber-500/60' : 'text-amber-700/70'
                }`}
              >
                · {LAB_TABS.length} benches · {LAB_TEMPLATE_TOTAL} experiments
              </span>
            </div>
            <div
              className={`text-[10px] mt-0.5 ${
                darkMode ? 'text-amber-200/60' : 'text-amber-700/80'
              }`}
            >
              Pick a bench, fire a probe · paste &amp; tweak, or run as-is
            </div>
          </div>

          {/* Tab strip — horizontal, wraps to a 2nd row when 7+ tabs
              don't fit single-line at the current flyout width. Pre-v2.1
              this used overflow-x-auto, which scrolled horizontally
              when 7 tabs hit; flex-wrap is the better answer because
              the 1-second-glance-lens demands all tabs visible without
              scroll-discovery friction. flex-shrink-0 keeps the strip
              outside the grid's vertical scroll area. */}
          <div
            role="tablist"
            aria-label="Lab benches"
            data-testid="lab-tab-strip"
            className={`flex flex-wrap gap-0.5 px-1 pt-1 flex-shrink-0 ${
              darkMode ? 'bg-gray-900' : 'bg-amber-50'
            }`}
          >
            {LAB_TABS.map((t) => {
              const isActive = t.id === activeTabId;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`lab-panel-${t.id}`}
                  id={`lab-tab-${t.id}`}
                  data-testid={`lab-tab-${t.id}`}
                  onClick={() => setActiveTabId(t.id)}
                  title={t.descriptor}
                  className={`flex-shrink-0 px-2 py-1 rounded-t text-[10.5px] font-semibold transition-colors whitespace-nowrap ${
                    isActive
                      ? darkMode
                        ? 'bg-gray-900/95 text-amber-300 border-b-2 border-amber-400'
                        : 'bg-white/80 text-amber-900 border-b-2 border-amber-500'
                      : darkMode
                        ? 'text-amber-500/70 hover:text-amber-300 hover:bg-amber-900/20'
                        : 'text-amber-700/70 hover:text-amber-900 hover:bg-amber-100/60'
                  }`}
                >
                  <span className="mr-1" aria-hidden>{t.emoji}</span>
                  {t.label}
                  <span className={`ml-1 text-[9px] font-mono ${
                    isActive
                      ? darkMode ? 'text-amber-500/70' : 'text-amber-700/70'
                      : 'opacity-60'
                  }`}>
                    {t.templates.length + (t.actions?.length ?? 0)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active-tab descriptor — sits between tab strip and grid */}
          <div
            className={`px-3 py-1.5 text-[10px] italic border-b ${
              darkMode
                ? 'bg-gray-900/95 text-amber-300/70 border-amber-500/20'
                : 'bg-white/80 text-amber-700/80 border-amber-200'
            }`}
            id={`lab-panel-${activeTab.id}-descriptor`}
          >
            <span className="mr-1" aria-hidden>{activeTab.emoji}</span>
            <span className="font-semibold">{activeTab.label}</span>
            <span className="mx-1 opacity-50">·</span>
            <span>{activeTab.descriptor}</span>
          </div>

          {/* Active tab's templates — 2-col grid with FIXED height +
              internal scroll. h-[24rem] holds the chrome → grid →
              chrome layout invariant across tab switches: tab strip,
              descriptor, and stripes don't move when the active tab's
              prompt count differs (10 vs 6 etc). content-start +
              auto-rows-min pack prompts to the top so smaller tabs
              show empty space below rather than stretching cards.
              tailwindcss-scrollbar plugin (loaded in tailwind.config.js)
              gives the rare visible scrollbar a quiet amber tint
              matching the bench palette. */}
          <div
            role="tabpanel"
            id={`lab-panel-${activeTab.id}`}
            aria-labelledby={`lab-tab-${activeTab.id}`}
            data-testid={`lab-panel-${activeTab.id}`}
            className={`grid grid-cols-2 gap-1 p-2 h-[24rem] overflow-y-auto content-start auto-rows-min scrollbar-thin ${
              darkMode
                ? 'bg-gray-900/95 scrollbar-thumb-amber-700/40 scrollbar-track-gray-900/40'
                : 'bg-white/70 scrollbar-thumb-amber-300 scrollbar-track-amber-50'
            }`}
          >
            {activeTab.templates.map((t) => (
              <button
                key={t.id ?? t.label}
                onClick={() => insert(t.prompt)}
                title={t.title}
                data-testid={`lab-template-${t.id ?? t.label}`}
                className={`group flex flex-col items-start gap-0.5 px-2 py-1.5 rounded text-left transition-colors border min-h-[3.25rem] ${
                  darkMode
                    ? 'border-transparent hover:border-amber-500/40 hover:bg-amber-900/30'
                    : 'border-transparent hover:border-amber-300 hover:bg-amber-100'
                }`}
              >
                <span
                  className={`text-[11px] font-semibold leading-tight ${
                    darkMode
                      ? 'text-gray-100 group-hover:text-amber-200'
                      : 'text-gray-900 group-hover:text-amber-900'
                  }`}
                >
                  {t.label}
                </span>
                {t.descriptor && (
                  <span
                    className={`text-[9.5px] leading-snug line-clamp-2 ${
                      darkMode
                        ? 'text-gray-400 group-hover:text-amber-300/80'
                        : 'text-gray-500 group-hover:text-amber-700/90'
                    }`}
                  >
                    {t.descriptor}
                  </span>
                )}
              </button>
            ))}
            {/* v0.1 Hill Climbers — action chips render with a violet
                palette so the operator's eye reads "execute" vs the
                amber "insert" templates. Same grid slot, same min-h. */}
            {(activeTab.actions ?? []).map((a) => {
              const key = `${activeTab.id}:${a.id}`;
              const busy = busyActionKey === key;
              return (
                <button
                  key={a.id}
                  onClick={() => runAction(activeTab.id, a)}
                  disabled={busy || busyActionKey !== null}
                  title={a.descriptor}
                  data-testid={`lab-action-${a.id}`}
                  className={`group flex flex-col items-start gap-0.5 px-2 py-1.5 rounded text-left transition-colors border min-h-[3.25rem] ${
                    busy
                      ? darkMode
                        ? 'border-violet-500/60 bg-violet-900/40 cursor-progress'
                        : 'border-violet-400 bg-violet-100 cursor-progress'
                      : darkMode
                        ? 'border-transparent hover:border-violet-500/40 hover:bg-violet-900/30'
                        : 'border-transparent hover:border-violet-300 hover:bg-violet-100'
                  } ${busyActionKey !== null && !busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`text-[11px] font-semibold leading-tight inline-flex items-center gap-1 ${
                      darkMode
                        ? 'text-gray-100 group-hover:text-violet-200'
                        : 'text-gray-900 group-hover:text-violet-900'
                    }`}
                  >
                    <span aria-hidden>{busy ? '⟳' : '▶'}</span>
                    {busy ? `${a.label}…` : a.label}
                  </span>
                  <span
                    className={`text-[9.5px] leading-snug line-clamp-2 ${
                      darkMode
                        ? 'text-gray-400 group-hover:text-violet-300/80'
                        : 'text-gray-500 group-hover:text-violet-700/90'
                    }`}
                  >
                    {a.descriptor}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Bottom construction band */}
          <div className="h-2 w-full" style={CONSTRUCTION_STRIPES} aria-hidden />
        </div>
      )}
    </div>
  );
};

export default ComposeMenu;
